import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  ClipboardDocumentIcon,
  EllipsisHorizontalIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ShareIcon,
  ArrowPathIcon,
  SpeakerWaveIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/20/solid'
import { Button, EmptyState, Textarea } from '../../../components/ui'
import { uiClasses } from '../../shared/uiClasses'
import type { ChangeEvent, FormEvent, KeyboardEvent, MouseEvent } from 'react'
import type { ChatMessage, ChatThread } from '../types'

interface ChatBubbleProps {
  message: ChatMessage
}

interface ChatTabProps {
  activeChatThreadId: string
  chatLoading: boolean
  chatThreads: ChatThread[]
  isMobileSidebarOpen: boolean
  messages: ChatMessage[]
  onDeleteChatThread: (threadId: string) => void
  onMobileSidebarOpenChange: (nextIsOpen: boolean) => void
  onRenameChatThread: (threadId: string) => void
  onResetChats: () => void
  onSendMessage: (text: string) => void
  onStartNewChat: () => void
  onSwitchChatThread: (threadId: string) => void
}

interface MarkdownMessageProps {
  content: string
  isUser: boolean
}

interface ThreadContextMenuState {
  threadId: string
  x: number
  y: number
}

const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const USER_BUBBLE = uiClasses.bubbleEmphasis
const USER_CODE_SURFACE = uiClasses.codeSurfaceEmphasis
const ASSISTANT_CODE_SURFACE = uiClasses.codeSurfaceDefault
const QUOTE_BORDER = uiClasses.borderMuted
const OPENROUTER_ERROR_PREFIX = '[OPENROUTER_'
const OPENROUTER_NOTIFICATION_FALLBACK =
  'OpenRouter request failed. Check your API key in Settings.'
const CHAT_INPUT_MIN_HEIGHT_PX = 34
const CHAT_INPUT_MAX_HEIGHT_PX = 184
const EMPTY_CHAT_SUGGESTIONS = [
  'Summarize my latest bloodwork',
  'What should I improve this week?',
]

function extractOpenRouterErrorNotification(content: string): string | null {
  const normalizedContent = content.trim()
  if (!normalizedContent.includes(OPENROUTER_ERROR_PREFIX)) {
    return null
  }

  const cleanedMessage = normalizedContent.replace(/\[[A-Z0-9_]+\]\s*/g, '').trim()
  return cleanedMessage || OPENROUTER_NOTIFICATION_FALLBACK
}

function MarkdownMessage({ content, isUser }: MarkdownMessageProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        ul: ({ children }) => <ul className="mb-2 ml-5 list-disc space-y-1 last:mb-0">{children}</ul>,
        ol: ({ children }) => <ol className="mb-2 ml-5 list-decimal space-y-1 last:mb-0">{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        h1: ({ children }) => <h1 className="mb-2 text-base font-semibold">{children}</h1>,
        h2: ({ children }) => <h2 className="mb-2 text-sm font-semibold">{children}</h2>,
        h3: ({ children }) => <h3 className="mb-2 text-sm font-semibold">{children}</h3>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        blockquote: ({ children }) => (
          <blockquote className={`mb-2 border-l-2 pl-3 italic last:mb-0 ${QUOTE_BORDER}`}>{children}</blockquote>
        ),
        pre: ({ children }) => (
          <pre
            className={`mb-2 overflow-x-auto whitespace-pre-wrap rounded-md border px-3 py-2 text-xs ${
              isUser ? USER_CODE_SURFACE : ASSISTANT_CODE_SURFACE
            }`}
          >
            {children}
          </pre>
        ),
        code: ({ children }) => (
          <code
            className={`rounded border px-1 py-0.5 text-xs ${
              isUser ? USER_CODE_SURFACE : ASSISTANT_CODE_SURFACE
            }`}
          >
            {children}
          </code>
        ),
        a: (props) => {
          const hrefValue = (props as { href?: unknown }).href
          const href = typeof hrefValue === 'string' ? hrefValue : undefined

          return (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className={`${isUser ? TEXT_PRIMARY : TEXT_SECONDARY} underline`}
            >
              {props.children}
            </a>
          )
        },
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

const ASSISTANT_ACTIONS = [
  { icon: ClipboardDocumentIcon, label: 'Copy' },
  { icon: HandThumbUpIcon, label: 'Good response' },
  { icon: HandThumbDownIcon, label: 'Bad response' },
  { icon: ShareIcon, label: 'Share' },
  { icon: ArrowPathIcon, label: 'Try again' },
  { icon: SpeakerWaveIcon, label: 'Read aloud' },
]

function ChatBubble({ message }: ChatBubbleProps) {
  const isUser = message.role === 'user'
  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
      <div
        className={`rounded-lg text-sm/6 ${
          isUser ? `max-w-[80%] px-4 py-3 ${USER_BUBBLE}` : `max-w-full px-4 py-2 ${TEXT_PRIMARY}`
        }`}
      >
        <MarkdownMessage content={message.content} isUser={isUser} />
      </div>
      {!isUser && (
        <div className="mt-1.5 flex gap-0.5">
          {ASSISTANT_ACTIONS.map(({ icon: Icon, label }) => (
            <Button
              key={label}
              type="button"
              variant="unstyled"
              className="dashboard-chat-action-btn rounded-md p-1.5 opacity-40 transition-opacity hover:opacity-80"
              aria-label={label}
              title={label}
            >
              <Icon className="size-3.5" />
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function ChatTab({
  activeChatThreadId,
  chatLoading,
  chatThreads,
  isMobileSidebarOpen,
  messages,
  onDeleteChatThread,
  onMobileSidebarOpenChange,
  onRenameChatThread,
  onResetChats,
  onSendMessage,
  onStartNewChat,
  onSwitchChatThread,
}: ChatTabProps) {
  const [chatInput, setChatInput] = useState('')
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [chatNotification, setChatNotification] = useState<string | null>(null)
  const [threadContextMenu, setThreadContextMenu] = useState<ThreadContextMenuState | null>(null)
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null)
  const messagesContainerRef = useRef<HTMLDivElement | null>(null)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const chatNotificationTimerRef = useRef<number | null>(null)
  const lastOpenRouterErrorMessageIdRef = useRef<string | null>(null)

  const visibleMessages = useMemo(
    () =>
      messages.filter(
        (message) =>
          !(
            message.role === 'assistant' &&
            extractOpenRouterErrorNotification(message.content)
          )
      ),
    [messages]
  )
  const previousMessageCountRef = useRef(visibleMessages.length)

  function dismissChatNotification() {
    if (chatNotificationTimerRef.current !== null) {
      window.clearTimeout(chatNotificationTimerRef.current)
      chatNotificationTimerRef.current = null
    }

    setChatNotification(null)
  }

  function showChatNotification(message: string) {
    if (chatNotificationTimerRef.current !== null) {
      window.clearTimeout(chatNotificationTimerRef.current)
    }

    setChatNotification(message)
    chatNotificationTimerRef.current = window.setTimeout(() => {
      setChatNotification(null)
      chatNotificationTimerRef.current = null
    }, 4200)
  }

  function closeThreadContextMenu() {
    setThreadContextMenu(null)
  }

  async function handleShareChatThread(threadId: string) {
    const targetThread = chatThreads.find((thread) => thread.id === threadId)
    if (!targetThread) {
      return
    }

    if (!navigator.clipboard?.writeText) {
      showChatNotification('Share is unavailable on this browser.')
      return
    }

    const transcript = targetThread.messages
      .map((message) => `${message.role === 'user' ? 'You' : 'Assistant'}: ${message.content}`)
      .join('\n\n')
    const sharePayload = transcript
      ? `${targetThread.title}\n\n${transcript}`
      : `${targetThread.title}\n\nNo messages yet.`

    try {
      await navigator.clipboard.writeText(sharePayload)
      showChatNotification(`Copied "${targetThread.title}" to clipboard.`)
    } catch (error) {
      showChatNotification(error instanceof Error ? error.message : 'Unable to copy chat to clipboard.')
    }
  }

  useEffect(
    () => () => {
      if (chatNotificationTimerRef.current !== null) {
        window.clearTimeout(chatNotificationTimerRef.current)
      }
    },
    []
  )

  useLayoutEffect(() => {
    const inputElement = chatInputRef.current
    if (!inputElement) {
      return
    }

    inputElement.style.height = 'auto'
    const nextHeight = Math.min(
      Math.max(inputElement.scrollHeight, CHAT_INPUT_MIN_HEIGHT_PX),
      CHAT_INPUT_MAX_HEIGHT_PX
    )
    inputElement.style.height = `${nextHeight}px`
    inputElement.style.overflowY =
      inputElement.scrollHeight > CHAT_INPUT_MAX_HEIGHT_PX ? 'auto' : 'hidden'
  }, [chatInput])

  useEffect(() => {
    if (!threadContextMenu) {
      return
    }

    const handleWindowKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setThreadContextMenu(null)
      }
    }

    const handleWindowResize = () => {
      setThreadContextMenu(null)
    }

    const handleWindowScroll = () => {
      setThreadContextMenu(null)
    }

    window.addEventListener('keydown', handleWindowKeyDown)
    window.addEventListener('resize', handleWindowResize)
    window.addEventListener('scroll', handleWindowScroll, true)
    return () => {
      window.removeEventListener('keydown', handleWindowKeyDown)
      window.removeEventListener('resize', handleWindowResize)
      window.removeEventListener('scroll', handleWindowScroll, true)
    }
  }, [threadContextMenu])

  useEffect(() => {
    const latestOpenRouterErrorMessage = [...messages]
      .reverse()
      .find(
        (message) =>
          message.role === 'assistant' &&
          extractOpenRouterErrorNotification(message.content)
      )

    if (!latestOpenRouterErrorMessage) {
      return
    }

    if (lastOpenRouterErrorMessageIdRef.current === latestOpenRouterErrorMessage.id) {
      return
    }

    lastOpenRouterErrorMessageIdRef.current = latestOpenRouterErrorMessage.id
    const notificationMessage = extractOpenRouterErrorNotification(
      latestOpenRouterErrorMessage.content
    )
    showChatNotification(notificationMessage || OPENROUTER_NOTIFICATION_FALLBACK)
  }, [messages])

  useEffect(() => {
    onMobileSidebarOpenChange(false)
  }, [activeChatThreadId, onMobileSidebarOpenChange])

  useEffect(() => {
    const container = messagesContainerRef.current
    const anchor = messagesEndRef.current
    if (!container || !anchor || typeof anchor.scrollIntoView !== 'function') {
      previousMessageCountRef.current = visibleMessages.length
      return
    }

    const hasNewMessage = visibleMessages.length > previousMessageCountRef.current
    const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight
    const shouldStickToBottom = distanceToBottom < 120

    if (hasNewMessage || (chatLoading && shouldStickToBottom)) {
      anchor.scrollIntoView({ behavior: hasNewMessage ? 'smooth' : 'auto' })
    }

    previousMessageCountRef.current = visibleMessages.length
  }, [chatLoading, visibleMessages])

  const threadContextMenuStyle = useMemo(() => {
    if (!threadContextMenu) {
      return undefined
    }

    const menuWidth = 182
    const menuHeight = 132
    const safeMargin = 10
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const left = Math.min(
      Math.max(threadContextMenu.x, safeMargin),
      Math.max(safeMargin, viewportWidth - menuWidth - safeMargin)
    )
    const top = Math.min(
      Math.max(threadContextMenu.y, safeMargin),
      Math.max(safeMargin, viewportHeight - menuHeight - safeMargin)
    )

    return {
      left,
      top,
    }
  }, [threadContextMenu])

  function submitMessage(messageText: string) {
    const nextMessage = messageText.trim()
    if (!nextMessage) {
      return
    }

    onSendMessage(nextMessage)
    setChatInput('')
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    submitMessage(chatInput)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submitMessage(chatInput)
    }
  }

  function handleInputChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setChatInput(event.target.value)
  }

  function applyEmptyStateSuggestion(suggestion: string) {
    setChatInput(suggestion)
    window.requestAnimationFrame(() => {
      chatInputRef.current?.focus()
    })
  }

  function handleThreadActionsTriggerClick(event: MouseEvent<HTMLElement>, threadId: string) {
    event.preventDefault()
    event.stopPropagation()
    if (chatLoading) {
      return
    }

    const triggerRect = event.currentTarget.getBoundingClientRect()
    const estimatedMenuWidth = 182

    setThreadContextMenu((currentMenu) => {
      if (currentMenu?.threadId === threadId) {
        return null
      }

      return {
        threadId,
        x: triggerRect.right - estimatedMenuWidth,
        y: triggerRect.bottom + 6,
      }
    })
  }

  function handleRenameThreadFromContextMenu() {
    if (!threadContextMenu) {
      return
    }

    onRenameChatThread(threadContextMenu.threadId)
    closeThreadContextMenu()
  }

  async function handleShareThreadFromContextMenu() {
    if (!threadContextMenu) {
      return
    }

    await handleShareChatThread(threadContextMenu.threadId)
    closeThreadContextMenu()
  }

  function handleDeleteThreadFromContextMenu() {
    if (!threadContextMenu) {
      return
    }

    onDeleteChatThread(threadContextMenu.threadId)
    closeThreadContextMenu()
  }

  const canSend = chatInput.trim().length > 0 && !chatLoading
  const shouldShowPrompt = chatInput.length === 0 && !isInputFocused

  return (
    <section className={`dashboard-chat-layout${isMobileSidebarOpen ? ' is-mobile-sidebar-open' : ''}`}>
      {chatNotification && (
        <div className="records-vault-toast-wrapper" aria-live="polite">
          <div className="records-vault-toast records-vault-toast-danger" role="status">
            <p className="records-vault-toast-message">{chatNotification}</p>
            <Button
              type="button"
              variant="unstyled"
              className="records-vault-toast-close"
              aria-label="Dismiss notification"
              onClick={dismissChatNotification}
            >
              <XMarkIcon className="size-4" />
            </Button>
          </div>
        </div>
      )}
      <aside id="dashboard-chat-sidebar-panel" className="dashboard-chat-sidebar" aria-label="Chat conversations">
        <div className="dashboard-chat-sidebar-header">
          <div className="dashboard-chat-sidebar-heading">
            <p className="dashboard-chat-sidebar-kicker">Conversations</p>
            <Button
              type="button"
              variant="unstyled"
              className="dashboard-chat-mobile-sidebar-close"
              onClick={() => onMobileSidebarOpenChange(false)}
              aria-label="Close conversations panel"
            >
              Close
            </Button>
          </div>
          <Button
            type="button"
            variant="unstyled"
            className="dashboard-chat-sidebar-new"
            onClick={() => {
              onStartNewChat()
              onMobileSidebarOpenChange(false)
            }}
            disabled={chatLoading}
          >
            <span className="dashboard-chat-sidebar-new-plus" aria-hidden="true">
              +
            </span>
            <span>New chat</span>
          </Button>
        </div>

        <div className="dashboard-chat-sidebar-list">
          {chatThreads.length === 0 ? (
            <EmptyState
              className="dashboard-chat-sidebar-empty !py-3"
              icon={ChatBubbleBottomCenterTextIcon}
              iconClassName={`size-5 ${TEXT_SUBTLE}`}
              title="No chats yet."
              titleClassName={`mt-2 text-xs/5 ${TEXT_SUBTLE}`}
            />
          ) : (
            chatThreads.map((thread) => {
              const isActiveThread = thread.id === activeChatThreadId
              const isThreadContextMenuOpen = threadContextMenu?.threadId === thread.id

              return (
                <div
                  key={thread.id}
                  className={`dashboard-chat-sidebar-row${isActiveThread ? ' is-active' : ''}${isThreadContextMenuOpen ? ' is-menu-open' : ''}`}
                >
                  <Button
                    type="button"
                    variant="unstyled"
                    className={`dashboard-chat-sidebar-item${isActiveThread ? ' is-active' : ''}`}
                    onClick={() => {
                      closeThreadContextMenu()
                      onSwitchChatThread(thread.id)
                      onMobileSidebarOpenChange(false)
                    }}
                    aria-current={isActiveThread ? 'true' : undefined}
                    title={thread.title}
                    disabled={chatLoading}
                  >
                    <span
                      className="dashboard-chat-sidebar-copy"
                      style={{ paddingInlineStart: 'var(--dashboard-chat-sidebar-item-inset)' }}
                    >
                      <span className={`dashboard-chat-sidebar-title ${TEXT_PRIMARY}`}>{thread.title}</span>
                    </span>
                  </Button>
                  <div className="dashboard-chat-sidebar-item-actions">
                    <Button
                      type="button"
                      variant="unstyled"
                      className="dashboard-chat-sidebar-item-menu-trigger"
                      aria-label={`Open options for ${thread.title}`}
                      aria-expanded={isThreadContextMenuOpen ? 'true' : undefined}
                      aria-haspopup="menu"
                      onClick={(event) => handleThreadActionsTriggerClick(event, thread.id)}
                      disabled={chatLoading}
                    >
                      <EllipsisHorizontalIcon className="size-4" />
                    </Button>
                  </div>
                </div>
              )
            })
          )}
        </div>

        <div className="dashboard-chat-sidebar-footer">
          <Button
            type="button"
            variant="unstyled"
            className="dashboard-chat-sidebar-reset"
            onClick={() => {
              onResetChats()
              onMobileSidebarOpenChange(false)
            }}
            disabled={chatLoading || chatThreads.length === 0}
          >
            Reset chats
          </Button>
        </div>
      </aside>

      <Button
        type="button"
        variant="unstyled"
        className="dashboard-chat-mobile-backdrop"
        aria-label="Close conversations panel"
        onClick={() => onMobileSidebarOpenChange(false)}
      />

      {threadContextMenu && (
        <div
          className="dashboard-chat-thread-context-menu-backdrop"
          onClick={closeThreadContextMenu}
          onContextMenu={(event) => {
            event.preventDefault()
            closeThreadContextMenu()
          }}
        >
          <div
            className="dashboard-chat-thread-context-menu"
            role="menu"
            style={threadContextMenuStyle}
            onClick={(event) => event.stopPropagation()}
          >
            <Button
              type="button"
              variant="unstyled"
              className="dashboard-chat-thread-context-menu-item"
              role="menuitem"
              onClick={() => void handleShareThreadFromContextMenu()}
            >
              Share
            </Button>
            <Button
              type="button"
              variant="unstyled"
              className="dashboard-chat-thread-context-menu-item"
              role="menuitem"
              onClick={handleRenameThreadFromContextMenu}
            >
              Rename
            </Button>
            <Button
              type="button"
              variant="unstyled"
              className="dashboard-chat-thread-context-menu-item is-destructive"
              role="menuitem"
              onClick={handleDeleteThreadFromContextMenu}
            >
              Delete
            </Button>
          </div>
        </div>
      )}

      <div className="dashboard-chat-main">
        <div className="dashboard-chat-thread" ref={messagesContainerRef}>
          {!chatLoading && visibleMessages.length === 0 && (
            <div className="dashboard-chat-empty-state" role="status" aria-live="polite">
              <p className={`dashboard-chat-empty-state-title ${TEXT_PRIMARY}`}>Start a conversation</p>
              <p className={`dashboard-chat-empty-state-copy ${TEXT_SUBTLE}`}>
                Ask about labs, medications, symptoms, or next steps from your latest results.
              </p>
              <div className="dashboard-chat-empty-state-suggestions">
                {EMPTY_CHAT_SUGGESTIONS.map((suggestion) => (
                  <Button
                    key={suggestion}
                    type="button"
                    variant="unstyled"
                    className="dashboard-chat-empty-state-suggestion"
                    onClick={() => applyEmptyStateSuggestion(suggestion)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {visibleMessages.map((msg) => (
            <ChatBubble key={msg.id} message={msg} />
          ))}
          {chatLoading && (
            <div className="flex justify-start">
              <div className={`dashboard-chat-loading text-sm/6 ${TEXT_SUBTLE}`}>
                <span className="inline-flex gap-1">
                  <span className="animate-bounce">·</span>
                  <span className="animate-bounce" style={{ animationDelay: '0.15s' }}>·</span>
                  <span className="animate-bounce" style={{ animationDelay: '0.3s' }}>·</span>
                </span>
              </div>
            </div>
          )}
          <p className="sr-only" aria-live="polite">
            {chatLoading ? 'Assistant is responding.' : ''}
          </p>
          <div ref={messagesEndRef} />
        </div>

        <div className="dashboard-chat-composer">
          <form onSubmit={handleSubmit} className="ui-hero-chat">
            <div className="ui-hero-chat-input-shell">
              <Textarea
                ref={chatInputRef}
                className="ui-hero-chat-input"
                rows={1}
                aria-label="Chat input"
                placeholder="Ask about your health..."
                value={chatInput}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                disabled={chatLoading}
              />
              <div className={`ui-hero-chat-placeholder ${shouldShowPrompt ? 'is-visible' : 'is-hidden'}`}>
                Ask about your health...
              </div>
            </div>
            <div className="ui-hero-chat-toolbar">
              <span className="ui-hero-chat-attach" aria-hidden="true">
                <span className="ui-hero-chat-attach-icon">+</span>
                <span>Add files</span>
              </span>
              <Button type="submit" variant="unstyled" className="ui-hero-chat-send" disabled={!canSend} aria-label="Send message">
                <span className="ui-hero-chat-send-icon" aria-hidden="true">&#8593;</span>
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  )
}
